import {
  Button,
  Checkbox,
  NumberInput,
  Select,
  Skeleton,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconPackageImport } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { useUnsavedChanges } from '../../shell-context.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import { purchasingError } from './errors.js';
import orders from './Orders.module.css';
import {
  GRN_CONDITIONS,
  type GrnFormLine,
  type GrnFormValues,
  grnFormDefaults,
  selectOrder,
  suggestCondition,
  toGrnInput,
  validateGrnForm,
} from './orders.js';
import classes from './Purchasing.module.css';
import { formatQuantity, numberOf, numberSeparators } from './requisitions.js';
import type { GoodsReceiptEditor } from './source.js';
import { LoadError } from './table.js';
import type { GoodsReceiptForm, GrnCondition } from './types.js';

/**
 * Yeni GRN (gemi; ADR 0040 #183–#185): sipariş seçilir, açık kalemler varsayılan olarak tam teslim
 * gelir; kabul edilen, hasarlı ve eksik miktar, kalite sonucu, parti ve son kullanma girilir.
 * Kabul edilen miktar aynı transaction'da stoğa girer. İnternetsiz çalışır (gemi sunucusu).
 */
export function NewGoodsReceipt({
  editor,
  orderId,
  onDone,
  onCancel,
}: {
  editor: GoodsReceiptEditor;
  /** Sipariş kartından gelindiyse o sipariş seçili gelir. */
  orderId: string | null;
  onDone: (id: string, message: string) => void;
  onCancel: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.grn.form.${key}`, options);
  const [form, setForm] = useState<GoodsReceiptForm | null>(null);
  const [initial, setInitial] = useState<GrnFormValues | null>(null);
  const [values, setValues] = useState<GrnFormValues | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ id: string; message: string } | null>(null);
  const finished = useRef(false);

  const load = useCallback(() => {
    let alive = true;
    setLoadError(null);
    editor
      .form()
      .then((loaded) => {
        if (!alive) return;
        const defaults = grnFormDefaults(loaded, orderId);
        setForm(loaded);
        setInitial(defaults);
        setValues(defaults);
      })
      .catch((error: unknown) => {
        if (alive) setLoadError(errorCode(error));
      });
    return () => {
      alive = false;
    };
  }, [editor, orderId]);
  useEffect(() => {
    void attempt;
    return load();
  }, [load, attempt]);

  // Kaydedilmemiş değişiklik: gezinme onay ister (M6f §0.3)
  const dirty =
    initial !== null && values !== null && JSON.stringify(initial) !== JSON.stringify(values);
  useUnsavedChanges(dirty && !busy && done === null);
  useEffect(() => {
    if (!done || finished.current) return;
    finished.current = true;
    onDone(done.id, done.message);
  }, [done, onDone]);

  if (loadError) {
    return (
      <div className={classes.screen}>
        <PageHeader title={k('title')} crumb={k('title')} />
        <LoadError title={k('error')} code={loadError} onRetry={() => setAttempt((n) => n + 1)} />
      </div>
    );
  }
  if (!form || !values) {
    return (
      <div className={classes.screen} aria-busy>
        <span className={classes.visuallyHidden}>{k('loading')}</span>
        <PageHeader title={k('title')} crumb={k('title')} />
        <Skeleton height={170} />
        <Skeleton height={200} />
      </div>
    );
  }

  const set = (patch: Partial<GrnFormValues>) => setValues({ ...values, ...patch });
  const setLine = (index: number, patch: Partial<GrnFormLine>) =>
    set({
      lines: values.lines.map((line, i) => {
        if (i !== index) return line;
        const next = { ...line, ...patch };
        // Miktar değişince kalite sonucu önerisi (seçilen tutarlı sonuç korunur)
        return 'condition' in patch ? next : { ...next, condition: suggestCondition(next) };
      }),
    });
  const fieldError = (path: string) =>
    errors[path]
      ? t(`purchasing.field.${errors[path]}`, {
          defaultValue: t(`purchasing.errors.${errors[path]}`, { defaultValue: errors[path] }),
        })
      : undefined;
  const order = form.orders.find((o) => o.id === values.orderId) ?? null;
  const acceptedTotal = values.lines.reduce((sum, line) => {
    const value = numberOf(line.accepted);
    return sum + (value === '' ? 0 : value);
  }, 0);
  const openTotal = values.lines.reduce((sum, line) => sum + line.open, 0);
  const label = (line: GrnFormLine, field: string) =>
    k('field', { name: line.name, field: k(`columns.${field}`) });

  return (
    <div className={classes.screen}>
      <PageHeader title={k('title')} description={k('description')} crumb={k('title')} />

      <form
        className={classes.screen}
        aria-label={k('title')}
        noValidate
        onSubmit={async (event) => {
          event.preventDefault();
          const found = validateGrnForm(values);
          setErrors(found);
          if (Object.keys(found).length > 0) {
            setSaveError(found._form ?? 'fieldsInvalid');
            return;
          }
          setSaveError(null);
          setBusy(true);
          try {
            const saved = await editor.create(toGrnInput(values));
            setDone({
              id: saved.id,
              message: t('purchasing.grn.detail.notices.created', { no: saved.grnNo }),
            });
          } catch (error) {
            setSaveError(errorCode(error));
          } finally {
            setBusy(false);
          }
        }}
      >
        <section className={classes.section} aria-labelledby="grn-header">
          <h2 id="grn-header" className={classes.sectionTitle}>
            <span className={classes.sectionNumber}>1.</span> {k('header')}
          </h2>
          {form.orders.length === 0 ? (
            <EmptyState title={k('noOrders')} />
          ) : (
            <div className={orders.formGrid}>
              <Select
                label={k('order')}
                required
                placeholder={k('orderPlaceholder')}
                data={form.orders.map((o) => ({
                  value: o.id,
                  label: k('orderOption', { no: o.poNo, supplier: o.supplierName }),
                }))}
                value={values.orderId}
                allowDeselect={false}
                error={fieldError('orderId')}
                onChange={(value) => setValues(selectOrder(values, form, value))}
              />
              <DayInput
                label={k('receivedDate')}
                required
                // Gelecek tarih yok: gemi sunucusunun günü (CLAUDE.md "Zaman")
                maxDate={form.today}
                {...(order ? { minDate: order.orderDate } : {})}
                value={values.receivedDate}
                error={fieldError('receivedDate')}
                onChange={(value) => set({ receivedDate: value ?? '' })}
              />
              <TextInput
                label={k('deliveryNote')}
                maxLength={80}
                value={values.deliveryNote}
                onChange={(event) => set({ deliveryNote: event.currentTarget.value })}
              />
              <Select
                label={k('receivedBy')}
                required
                data={form.receivers.map((person) => ({
                  value: person.id,
                  label: person.role ? `${person.name} · ${person.role}` : person.name,
                }))}
                value={values.receivedBy || null}
                allowDeselect={false}
                error={fieldError('receivedBy')}
                onChange={(value) => set({ receivedBy: value ?? '' })}
              />
              <Textarea
                wrapperProps={{ 'data-wide': true }}
                label={k('remarks')}
                maxLength={1000}
                autosize
                minRows={1}
                maxRows={4}
                value={values.remarks}
                onChange={(event) => set({ remarks: event.currentTarget.value })}
              />
            </div>
          )}
        </section>

        {order ? (
          <section className={classes.section} aria-labelledby="grn-items">
            <div className={classes.itemsHead}>
              <h2 id="grn-items" className={classes.sectionTitle}>
                <span className={classes.sectionNumber}>2.</span> {k('items')}
              </h2>
              <span className={classes.muted}>
                {k('summary', {
                  accepted: formatQuantity(acceptedTotal, locale),
                  open: formatQuantity(openTotal, locale),
                })}
              </span>
            </div>
            <div className={classes.headerActions}>
              <Button
                variant="default"
                size="xs"
                onClick={() =>
                  set({
                    lines: values.lines.map((line) => ({
                      ...line,
                      accepted: line.open,
                      damaged: 0,
                      missing: 0,
                      condition: 'accepted',
                    })),
                  })
                }
              >
                {k('receiveAll')}
              </Button>
              <Button
                variant="default"
                size="xs"
                onClick={() =>
                  set({
                    lines: values.lines.map((line) => ({
                      ...line,
                      accepted: 0,
                      damaged: 0,
                      missing: 0,
                      condition: 'accepted',
                    })),
                  })
                }
              >
                {k('clear')}
              </Button>
            </div>
            {values.lines.length === 0 ? (
              <EmptyState title={k('noOrders')} />
            ) : (
              <section
                className={table.tableWrap}
                aria-label={k('items')}
                // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                tabIndex={0}
              >
                <table className={`${table.table} ${classes.table} ${classes.lineTable}`}>
                  <thead>
                    <tr>
                      <th scope="col" className={classes.key}>
                        {k('columns.material')}
                      </th>
                      <th scope="col">{k('columns.ordered')}</th>
                      <th scope="col">{k('columns.open')}</th>
                      <th scope="col">{k('columns.accepted')}</th>
                      <th scope="col">{k('columns.damaged')}</th>
                      <th scope="col">{k('columns.missing')}</th>
                      <th scope="col">{k('columns.condition')}</th>
                      <th scope="col">{k('columns.batch')}</th>
                      <th scope="col">{k('columns.expiry')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {values.lines.map((line, index) => {
                      const quantity = (field: 'accepted' | 'damaged' | 'missing') => (
                        <NumberInput
                          className={classes.qty}
                          aria-label={label(line, field)}
                          min={0}
                          decimalScale={3}
                          hideControls
                          {...numberSeparators(locale)}
                          value={line[field]}
                          error={fieldError(`lines.${index}.${field}`)}
                          onChange={(value) => setLine(index, { [field]: value })}
                        />
                      );
                      return (
                        <tr key={line.purchaseOrderLineId}>
                          <td className={`${classes.wrap} ${classes.key}`}>
                            {line.name}
                            <span className={classes.muted}>{` · ${line.unit}`}</span>
                            {/* İkame istenen kalem olarak stoğa girmez (ADR 0115 §4) */}
                            {line.substitute ? (
                              <small className={classes.pasteNote}>
                                {t('purchasingShip.grn.substitute', {
                                  name: line.substituteFor ?? '—',
                                })}
                              </small>
                            ) : null}
                          </td>
                          <td>{formatQuantity(line.ordered, locale)}</td>
                          <td>{formatQuantity(line.open, locale)}</td>
                          <td>{quantity('accepted')}</td>
                          <td>{quantity('damaged')}</td>
                          <td>{quantity('missing')}</td>
                          <td>
                            <Select
                              className={orders.conditionInput}
                              aria-label={label(line, 'condition')}
                              data={GRN_CONDITIONS.map((value) => ({
                                value,
                                label: t(`purchasing.grn.conditions.${value}`),
                              }))}
                              value={line.condition}
                              allowDeselect={false}
                              error={fieldError(`lines.${index}.condition`)}
                              onChange={(value) =>
                                setLine(index, { condition: (value ?? 'accepted') as GrnCondition })
                              }
                            />
                          </td>
                          <td>
                            <TextInput
                              className={orders.textInput}
                              aria-label={label(line, 'batch')}
                              maxLength={80}
                              value={line.batch}
                              onChange={(event) =>
                                setLine(index, { batch: event.currentTarget.value })
                              }
                            />
                          </td>
                          <td>
                            <DayInput
                              className={orders.dateInput}
                              aria-label={label(line, 'expiry')}
                              clearable
                              disabled={line.noExpiry}
                              value={line.noExpiry ? null : line.expiry}
                              onChange={(value) => setLine(index, { expiry: value })}
                            />
                            {/* 04.11 notları D7: son kullanma tarihi olmayan mal */}
                            <Checkbox
                              mt={6}
                              size="xs"
                              label={k('noExpiry')}
                              aria-label={label(line, 'noExpiry')}
                              checked={line.noExpiry}
                              onChange={(event) => {
                                const checked = event.currentTarget.checked;
                                setLine(index, {
                                  noExpiry: checked,
                                  ...(checked ? { expiry: null } : {}),
                                });
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>
            )}
          </section>
        ) : null}

        {saveError ? (
          <p className={classes.error} role="alert">
            {saveError === 'fieldsInvalid'
              ? k('fieldsInvalid')
              : purchasingError(t, saveError, 'grn')}
          </p>
        ) : null}

        <div className={classes.footer}>
          <Button variant="default" onClick={onCancel}>
            {k('cancel')}
          </Button>
          <Button
            type="submit"
            loading={busy}
            disabled={!order}
            leftSection={<IconPackageImport size={16} />}
          >
            {k('save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
